import axios from "axios";
import { Fragment, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import ContentContainer from "../../components/content/ContentContainer";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { IAudienceMember, IListing, IResponse, ReportReasonEntityProperties } from "./interfaces";
import styles from "./create-report.module.css";

import "react-bootstrap-typeahead/css/Typeahead.css";
import MemberAutoComplete from "../../components/widgets/autoComplete/MemberAutoComplete";
import { compliance_url, crib_url, getMemberByLoginId } from "../../adapters";
import { useAppDispatch } from "../../app/hooks";
import { show } from "../notification/notificationSlice";
import { Listbox, Transition } from "@headlessui/react";
import { SelectorIcon } from "@heroicons/react/solid";
import { classNames } from "../../util/helpers";
import { RequiredIndicator } from "../registeredListing/RegisteredListingForm";
import ReactTooltip from "react-tooltip";
import { QuestionMarkCircleIcon } from "@heroicons/react/outline";
import Loader from "../../components/widgets/Loader";
import { BeatLoader } from "react-spinners";
import { useSelector } from "react-redux";
import { user } from "../user/selectors";
import ListingView from "../../components/widgets/ListingView/ListingView";
import MemberView from "../../components/widgets/MemberView/MemberView";

enum SearchType {
	Listing = "Listings",
	Member = "Members",
}

const CreateReport = () => {
	const [listingSearchValue, setListingSearchValue] = useState("");
	const [description, setDescription] = useState("");
	const [reasonId, setReasonId] = useState("");
	const [memberId, setMemberId] = useState("");
	const [searchType, setSearchType] = useState<SearchType | "">("");
	const [reasons, setReasons] = useState<ReportReasonEntityProperties[]>([]);
	const [filteredReasons, setFilteredReasons] = useState<ReportReasonEntityProperties[]>([]);
	const [selectedMember, setSelectedMember] = useState<IAudienceMember>();
	const [selectedListing, setSelectedListing] = useState<IListing>();
	const [isSending, setIsSending] = useState(false);
	const [loadingListing, setLoadingListing] = useState(false);
	const [showForm, setShowForm] = useState(true);
	const [cancelToken, setCancelToken] = useState(undefined as any);
	const [selectedReason, setSelectedReason] = useState({
		reasonText: "Select a reason",
	} as any);
	const currentUser = useSelector(user);
	const dispatch = useAppDispatch();

	const getGroupedReasons = () => {
		return Object.values(
			filteredReasons.reduce((acc: any, obj: any) => {
				const key = obj.reportReasonCategory?.displayText;
				const listOrder = obj.reportReasonCategory?.listOrder ?? 9999;
				if (!acc[key]) {
					acc[key] = {
						groupTitle: key,
						listOrder,
						reasons: [],
					};
				}
				// Only add visible reasons
				if (obj.isVisibleToMembers !== false) {
					acc[key].reasons.push(obj);
				}
				return acc;
			}, {})
		)
			.filter((item: any) => item.reasons.length > 0)
			.sort((a: any, b: any) => (a.listOrder ?? 9999) - (b.listOrder ?? 9999));
	};
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const listingId = queryParams.get("listingid");

	const displayToast = (status: any, title: string, message: string) => {
		dispatch(
			show({
				show: true,
				title,
				message,
				status,
				position: "popover",
				autoHide: 5000,
				confirm: false,
				notificationId: null,
			})
		);
	};

	useEffect(() => {
		getReasons();
		getPageConfig();
		if (listingId) {
			setSearchType(SearchType.Listing);
			setListingSearchValue(listingId);
			getListing(listingId);
		}
	}, []);

	const [formConfig, setFormConfig] = useState<any>(null);

	const getReasons = () => {
		axios
			.post<IResponse<ReportReasonEntityProperties>>(`${compliance_url}ReportReasons/MemberHubReportReason/q`, {
				pageId: 0,
				pageSize: 50,
				criteria: [],
				orderBy: [],
				fields: [],
			})
			.then((response) => {
				const visibleReasons = response.data.results.filter((reason: any) => reason.isVisibleToMembers !== false);
				setReasons(visibleReasons);
				if (listingId || searchType) {
					setFilteredReasons(visibleReasons.filter((reason: any) => reason.requiresListing ));
				} else {
					setFilteredReasons([]);
				}
			})
			.catch((e) => {});
	};

	const getPageConfig = () => {
		axios
			.get(`${compliance_url}PageConfigs/E3C9E322-5E40-4B94-BC7B-A40BDDFC8C11`)
			.then((response) => {
				if (response.data && response.data.results && response.data.results.length > 0 && response.data.results[0]) {
					setFormConfig(JSON.parse(response.data.results[0].config));
				}
			})
			.catch((e) => {
				setFormConfig({
					disclaimer:
						'<p className="text-sm">Thank you for reporting a potential MLS rule violation. Be sure to include the property address, MLS number, or case number in addition to your report. This report will be sent to the CRMLS Compliance Department for review and if the issue is deemed to be an actual violation, appropriate measures will be taken to correct or address the issue.</p><p className="text-sm mt-2">Please do not resubmit the same issue.</p><p className="text-sm mt-2">All submissions are kept strictly confidential and we will not release your name to anyone outside of CRMLS Staff. In some cases, we may need to contact you for additional information about the potential violation you have reported.</p>',
					confimation: "Success! Your report has been created. An analyst will review your report.",
				});
			});
	};

	const getListing = (id: string) => {
		setLoadingListing(true);
		if (typeof cancelToken != typeof undefined) {
			cancelToken.cancel("Operation canceled due to new request.");
		}
		const newToken = axios.CancelToken.source();
		setCancelToken(newToken);
		axios
			.get<IResponse<IListing>>(`${crib_url}/long/${id}?include=media`, {
				cancelToken: newToken.token,
			})
			.then((response) => {
				setLoadingListing(false);
				setSelectedListing(response.data.results[0]);
			})
			.catch((error) => {
				if (axios.isCancel(error)) {
					console.log("Request canceled", error.message);
				} else {
					setLoadingListing(false);
				}
			});
	};

	const listingSearch = (search: string) => {
		if (searchType === SearchType.Listing) {
			getListing(search);
		}
	};

	const onMemberSelection = (id: string, value: string) => {
		setSelectedMember(undefined);

		if (value) {
			getMemberByLoginId(value).then((resp) => {
				if (resp?.data?.results?.length) {
					setSelectedMember({
						...resp?.data?.results[0],
						contactJObject: JSON.parse(resp?.data?.results[0].contactJObject),
					});
					setMemberId(resp?.data?.results[0]?.contactId);
				}
			});
		}
		return {};
	};

	const sendReport = () => {
		setIsSending(true);
		const payload = {
			reportReasonId: reasonId,
			listingId: selectedListing?.listingId,
			memberId: memberId.length ? memberId : undefined,
			text: description,
			reportingParty: currentUser.profile.memberFirstName + " " + currentUser.profile.memberLastName,
		};

		axios
			.post(`${compliance_url}Reports/CreateReport`, payload)
			.then(() => {
				setIsSending(false);
				displayToast("success", "Success", "Report created successfully");
				setShowForm(false);
			})
			.catch(() => {
				setIsSending(false);
				displayToast("error", "Error", "There was a problem creating your report");
			});
	};

	const isValid = () => {
		if (!description.length || !reasonId.length) {
			return false;
		}

		if ((searchType === SearchType.Listing && !selectedListing) || (searchType === SearchType.Member && !memberId.length)) {
			return false;
		}

		return true;
	};

	const resetForm = () => {
		setDescription("");
		setSearchType("");
		setReasonId("");
		setShowForm(true);
		setSelectedReason({ reasonText: "Select a reason" });
		setSelectedListing(undefined);
		setListingSearchValue("");
		setSelectedMember(undefined);
		setFilteredReasons([]);
	};

	const handleChange = (event: any) => {
		let sel = filteredReasons.find((option) => option.id === event);
		if (sel) {
			setSelectedReason(sel);
			setReasonId(sel.id);
		}
	};

	const handleFormTypeChange = (value: SearchType) => {
		setSearchType(value);
		setListingSearchValue("");
		setSelectedListing(undefined);
		setSelectedMember(undefined);
		setMemberId("");
		setSelectedReason({ reasonText: "Select a reason" });
		// Filter reasons for selected type
		setFilteredReasons(reasons.filter((reason: any) => (value === SearchType.Listing ? reason.requiresListing : !reason.requiresListing)));
	};
	const loader = <Loader />;

	if (!formConfig || reasons.length === 0) {
		return loader;
	}
	const form = (
		<>
			<ContentContainer title="Report A Violation" subTitle="" actions={null} cssClass="crmls-articles">
				<div className={`max-w-full text-sm text-primary p-4 sm:p-6 ${styles.main_container}`}>
					{/* Lower form fields - alpha out if type not selected */}
					<div className="grid grid-cols-1 sm:grid-cols-12 gap-0 sm:gap-4">
						{/* Report Type Selector - always enabled and opaque */}
						<div className="col-span-3">
							<label className="block text-sm font-medium text-gray-700 text-primary">
								Report Type
								{RequiredIndicator}
								<QuestionMarkCircleIcon 
									className="inline-block w-4 h-4 ml-1 text-gray-400 cursor-help" 
									data-tip="Are you reporting a member or a potential violation on a listing?"
								/>
							</label>
							<select
								className="basic-form-field px-4 py-2 rounded-md border mt-1"
								value={searchType}
								onChange={(e) => handleFormTypeChange(e.target.value as SearchType)}
								style={{ opacity: 1, pointerEvents: "auto" }}
								autoFocus
							>
								<option value="">Select...</option>
								<option value={SearchType.Listing}>Listing</option>
								<option value={SearchType.Member}>Member</option>
							</select>
						</div>
						<div className="col-span-6" style={{ opacity: searchType ? 1 : 0.5, pointerEvents: searchType ? "auto" : "none" }}>
							<label className="block text-sm font-medium text-gray-700 text-primary">Report Reason{RequiredIndicator}</label>
							<div className="relative mt-1 rounded-md shadow-sm mb-5">
								<Listbox value={selectedReason} onChange={handleChange} disabled={!searchType}>
									{({ open }) => (
										<>
											<div className="mt-1 relative">
												<span className="basic-form-field-listbox">
													<Listbox.Button className="bg-secondary text-secondary relative w-full border border-default rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
														<span className="block truncate">{selectedReason?.reasonText}</span>
														<span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
															<SelectorIcon className="h-5 w-5" aria-hidden="true" />
														</span>
													</Listbox.Button>
												</span>
												<Transition show={open} as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
													<Listbox.Options className="absolute z-10 mt-1 w-full bg-secondary text-secondary shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
														{getGroupedReasons().map((item: any, i: number) => (
															<span key={i}>
																<Listbox.Label>
																	<span className={"ml-2 font-bold"}>{item.groupTitle}</span>
																</Listbox.Label>
																{item.reasons.map((reason: any) => (
																	<Listbox.Option key={reason.id} value={reason.id} className={({ active }) => classNames(active ? "text-primary bg-primary" : "text-secondary", "cursor-default select-none relative py-2 pl-3 pr-9")}>
																		{reason.reasonText}
																	</Listbox.Option>
																))}
															</span>
														))}
													</Listbox.Options>
												</Transition>
											</div>
										</>
									)}
								</Listbox>
							</div>
						</div>
						{/* Other fields - alpha out and disable if no type selected */}
						<div className="col-span-3" style={{ opacity: searchType ? 1 : 0.5, pointerEvents: searchType ? "auto" : "none" }}>
							<label className="block text-sm font-medium text-gray-700 text-primary">
								{searchType === SearchType.Listing ? "Search by Listing #" : "Member Being Reported"}
								{RequiredIndicator}
							</label>
							<div className="grid grid-cols-12 relative mt-1 rounded-md shadow-sm mb-5">
								<div className="col-span-12">
									{searchType === SearchType.Listing ? (
										<input
											type="text"
											value={listingSearchValue}
											onChange={(e) => {
												setSelectedListing(undefined);
												setListingSearchValue(e.target.value);
												listingSearch(e.target.value);
											}}
											className={`basic-form-field`}
											placeholder={"Enter a listing id"}
											disabled={!searchType}
										/>
									) : (
										<MemberAutoComplete touched={{}} n={{}} values={[]} setFieldValue={onMemberSelection} defaultContainerStyle={false} disabled={searchType !== SearchType.Member} placeholder="Enter name, email or login ID of the member you are reporting" />
									)}
									<span
										style={{
											position: "absolute",
											right: "7px",
											top: "12px",
										}}
									>
										{loadingListing && <BeatLoader color={"#adadad"} size="5" />}
									</span>
								</div>
							</div>
							{!selectedListing && searchType === SearchType.Listing && listingSearchValue.length > 0 && !loadingListing && <div style={{ height: 0, display: "block", top: "-14px", position: "relative" }}>No listing found</div>}
						</div>
					</div>

					<div>
						<label className="block text-sm font-medium text-gray-700 text-primary" style={{ opacity: searchType ? 1 : 0.5 }}>
							Report Description{RequiredIndicator}
						</label>
						<div className="relative mt-1 rounded-md shadow-sm mb-5">
							<textarea
								value={description}
								onChange={(e) => {
									setDescription(e.target.value);
								}}
								className="basic-form-field"
								placeholder="Provide details of the potential violation you are reporting. Please be as specific as possible."
								disabled={!searchType}
								style={{ opacity: searchType ? 1 : 0.5 }}
							/>
						</div>
					</div>
					<MemberView selectedMember={selectedMember} />

					{selectedListing && !loadingListing ? (
						<div className="mb-5">
							<label className="block text-sm font-bold text-gray-700 text-primary">Listing Found</label>
							<ListingView listing={selectedListing} />
						</div>
					) : undefined}

					<div className="border px-4 py-3 rounded relative mb-5">{formConfig?.disclaimer && <div dangerouslySetInnerHTML={{ __html: formConfig.disclaimer }} className="text-sm text-primary" />}</div>

					<div>
						<button
							type="submit"
							className={`${!isValid() ? styles.disabled : styles.enabled} ${
								styles.submit_button
							} basic-form-field float-right inline-flex justify-center py-2 px-8 border border-transparent shadow-sm text-sm font-medium rounded-md text-inverse bg-header hover:bg-opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:cursor-not-allowed`}
							onClick={() => sendReport()}
							disabled={!isValid() || isSending}
						>
							{isSending ? "Sending..." : "Send Report"}
						</button>
					</div>
				</div>
			</ContentContainer>
		</>
	);

	const success = (
		<>
			<ContentContainer title="Create a Compliance Report" subTitle="" cssClass="crmls-articles">
				<div className={`max-w-full text-sm text-primary p-4 sm:p-6 ${styles.main_container} justify-center`}>
					{formConfig?.disclaimer && <div dangerouslySetInnerHTML={{ __html: formConfig.confirmation }} className="text-sm text-primary" />}
					<br />
					<button className={` mt-4 py-2 px-8 border border-transparent shadow-sm text-sm font-medium rounded-md text-inverse bg-header hover:bg-opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:cursor-not-allowed`} onClick={() => resetForm()}>
						Create Another Report
					</button>
				</div>
			</ContentContainer>
		</>
	);

	return (
		<>
			{showForm ? form : success}
			<ReactTooltip place="top" type="dark" effect="solid" />
		</>
	);
};

export default CreateReport;
